(ns app.rpc
  (:require-macros [adzerk.env :as env])
  (:require ["/js/utils/currency"       :as currency]
            ["jquery"                   :as jquerying]
            [adzerk.cljs-console        :as log :include-macros true]
            [castra.core                :as castra :refer [mkremote]]
            [cemerick.url               :refer [url]]
            [clojure.core.async         :refer [>! <! chan close! go]]
            [goog.net.Cookies]
            [cljstache.core             :as stache]
            [clojure.string             :as str]
            [geir.business-logic        :as biz]
            [geir.constants             :as const]
            [geir.i18n                  :refer [from-lang-pack*]]
            [hoplon.core
             :refer [with-timeout do-watch with-interval]]
            [hoplon.google.jsapi.loader :as google-jsapi-loader]
            [javelin.core
             :refer [cell cell= defc defc= dosync formula-of with-let]]
            [medley.core                :as medley]
            [ui.paging                  :as p :refer-macros [defp]]
            [ui.util                    :as util]
            [ui.global-state
             :refer [session-info
                     user-and-network-info
                     language-pack
                     ping-response]
             :as global]))

;; TODO: Move more of these upper-cased constant values into ui.global-state.

(defonce ^:dynamic *cookies* (.getInstance goog.net.Cookies))

(goog-define ADZERK_GEIR_BACKEND_LOCAL_PORT 9091)

;; Mirrors the backend's KEVEL_RILL_LOCAL_BASE_URL env var (see `dev/env
;; --rill`), so the frontend can tell whether the iframeSrc it gets back from
;; the backend is a local Rill dev server rather than Rill Cloud.
(goog-define KEVEL_RILL_LOCAL_BASE_URL "")

(def ADZERK_GEIR_BACKEND_URL
  (condp re-matches global/HOSTNAME
    ;; Dev
    #"localhost"
    (str "http://localhost:" ADZERK_GEIR_BACKEND_LOCAL_PORT)

    ;; Production on AWS
    #"app\.kevel\.co"
    "https://app-backend.kevel.co"

    ;; Production on GCP
    #"app\.gcp\.kevel\.co"
    "https://app-backend.gcp.kevel.co"

    ;; Internal URLs (for non-prod, etc.)
    #"geir-frontend-.+\..+\.opzerk\.com"
    (str "https://"
         (-> js/window
             .-location
             .-host
             (str/replace "-frontend" "-backend")))

    ;; Tailscale (e.g. minikevel-fleet over MagicDNS): frontend and backend share
    ;; a host; the backend is on ADZERK_GEIR_BACKEND_LOCAL_PORT. Use the page
    ;; protocol so an HTTPS frontend calls the backend over HTTPS.
    #".+\.ts\.net"
    (str (.. js/window -location -protocol) "//"
         global/HOSTNAME ":" ADZERK_GEIR_BACKEND_LOCAL_PORT)

    ;; Otherwise, log a warning to the JS console.
    (js/console.warn "Unable to determine the backend based on hostname."
                     #js {:hostname global/HOSTNAME})))

;; This is expected to be defined in the environment where the ClojureScript is
;; compiled. It can be seen in the UI when logged in as an omniscient or
;; omnipotent user.
(env/def ADZERK_GEIR_COMMIT "development-version")

;; file uploads ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn- assoc-when
  [m k v]
  (if-not v m (assoc m k v)))

(defn- xhr-resp-headers
  [xhr headers]
  (reduce #(if-let [x (.getResponseHeader xhr %2)] (assoc %1 %2 x) %1) {} headers))


(defn multipart-ajax-fn
  "Ajax request implementation using the standard jQuery ajax machinery."
  [{:keys [url timeout credentials headers body]}]
  (let [prom (.Deferred jquerying)
        opts (-> {"async"       true
                  "contentType" false
                  "data"        body
                  "headers"     headers
                  "processData" false
                  "type"        "POST"
                  "url"         url
                  "timeout"     timeout}
                 (assoc-when "xhrFields" (assoc-when nil "withCredentials" credentials)))
        resp (fn [x]
               {:status      (.-status x)
                :status-text (.-statusText x)
                :body        (.-responseText x)
                :headers     (xhr-resp-headers x ["X-Castra-Tunnel" "X-Castra-Session"])})]
    (-> (.ajax jquerying (clj->js opts))
        (.done (fn [_ _ x] (.resolve prom (resp x))))
        (.fail (fn [x _ _] (.reject  prom (resp x)))))
    prom))

(defn clj->form-data
  [[endpoint x]]
  (let [d (js/FormData.)]
    (.append d "castra-endpoint" (name endpoint))
    (doseq [[k v] x] (.append d (name k) (if (nil? v) "" v)))
    d))

;; stem cells ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defc= user              (:user ping-response))
(defc= feature-flags     (:feature-flags ping-response))
(defc= feature-flag-set  (->> feature-flags
                              (medley/filter-vals identity)
                              keys
                              (into #{})))
(defc= custom-fields     (:custom-fields ping-response))
(defc= session-id        (:session-id ping-response))
(defc= max-age           (:max-age ping-response))
(defc= session-expiration-reason (:session-expiration-reason ping-response))
(defc= user-access-level (:access-level user))
(defc= org-access-level  (get-in user [:organization :access-level]))

(defc cloud-provider   nil)
(defc frontend-version ADZERK_GEIR_COMMIT)
(defc backend-version  nil)
(do-watch
 session-id
 (fn [_ session-id]
   (when session-id
     (js/console.info (str "Session ID: " session-id))
      ; Setup parameter(s) for Datadog RUM to enable traceable metrics.
     (js/window.DD_RUM.setUserProperty "session_id" session-id))))
;; derived state ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defc= omniscient?         (= const/ACCESS-MODE-OMNISCIENT (:accessmode user)))
(defc= omnipotent?         (= const/ACCESS-MODE-OMNIPOTENT (:accessmode user)))

(defc= is-sso?             (biz/is-sso? (select-keys user [:iskevelidp :isgoogleidp])))
(defc= network             (:networkid user))
(defc= networkcount        (:networkcount user))
(defc= network-too-large   (> (:total-flights user) const/NETWORK-TOO-LARGE-LIMIT))
(defc= user-loaded?        (and (not= user :ui.global-state/unknown)
                                (map? user)
                                (:email user)))
(defc= logged-in?          (and user-loaded?
                                (or (and (number? network) (pos? network))
                                    ;; Org admins with no networks are also considered logged in
                                    (and (= org-access-level :org-access-level/admin)
                                         (= networkcount 0)))))
(defc= logged-out?         (and user-loaded? (not logged-in?)))
(defc= default-timezone
  (or (:usertimezone user)
      (:networktimezone user)
      "UTC"))

;; The ISO 4217 code money is displayed in for the current network, or nil when
;; the network sets no valid currency (money then shows as US dollars in en-US
;; style, as it always has). The backend validates it. Display only.
(defc= network-currency    (:networkcurrency user))
;; Shared with the TypeScript UI, which formats money from the same module.
(do-watch network-currency (fn [_ code] (currency/setNetworkCurrency code)))

;; Currently unused. We initially implemented this for sc-6811, and it works,
;; but the user experience would have been bad, so we didn't move forward with
;; it.
;;
;; The problem is that even if we know that the frontend version is different
;; from the backend, it isn't necessarily true that the user can fix it by
;; refreshing their browser. They might refresh their browser and the popup will
;; still be there because e.g. the frontend or backend is still updating.
;;
;; As a stopgap solution, we are having the frontend check whether there is a
;; new version of the frontend available by doing an XHR of the base URL and
;; checking the ETag. See `etag-mismatch?` further down in this namespace.
(defc= version-mismatch?   (and logged-in?
                                frontend-version
                                backend-version
                                (not= backend-version frontend-version)))

;; language packs ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defc= dictionary      (:dictionary user))
(defc= language        (:accept-language user))

(defn from-lang-pack
  "Returns a cell whose value is the value of a key `k` in the language pack
   that is currently loaded.

   The language pack that is currently loaded depends on the browser's
   `accept-language` header, and in the future, it may also be configurable as a
   user setting. Whenever the language pack changes, so does the text in all of
   the cells returned by this function.

   If the language pack entry contains mustache template placeholders, they can
   be provided as a map of `template-values`.

   Note that `template-values` can be a cell, which is useful in cases where the
   values change dynamically along with the application, and we want parts of
   the language pack entry text to change accordingly.

   Example:

   (rpc/from-lang-pack
     ::upsert-form-field-ad-sizes-extra
     (cell= {:width width-cell, :height height-cell}))

   The English language pack entry for this key has a value of:

   {{{width}}} px by {{{height}}} px

   In the UI, when the English language pack is displayed, the text might read
   like the following, with numbers that change dynamically based on the height
   and width cells:

   300 px by 250 px
   200 px by 100 px
   0 px by 0 px
   etc.

   `k` can also be a cell, which is useful in cases where we want different
   language pack entries to display in different situations.

   For example:

   (rpc/from-lang-pack
     (cell= (if (empty? data-cell)
              ::no-items-found
              ::items-label)))"
  [k & [template-values]]
  (cell= (let [translation (from-lang-pack* k dictionary language)]
           (if template-values
             (stache/render translation template-values)
             translation))))

(defn translated-map-cell
  "Given a flat map where the values are language pack keys, returns a cell
   whose value is the same map, with the language pack keys replaced by their
   values in the current language pack.

   e.g. (translation-cell {1 ::cheese, 2 ::wine}) returns a cell whose current
   value, depending on the current language pack, might be {1 \"cheese\", 2
   \"wine\"} or {1 \"fromage\", \"vin\"}."
  [m]
  (formula-of [dictionary language]
              (->> m
                   (map (fn [[k v]]
                          [k (from-lang-pack* v dictionary language)]))
                   (into {}))))

;; helpers ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defc rpc-loading             nil)
(defc app-errors              #{})
(defc tmp-flash-message       nil)

(defn user-has-access?
  "Does the user have the required access level? True under any of three conditions:

   - The required level is nil
   - The user is omnipotent
   - The user's access level is a subtype of the required level"
  [required level omnipotent?]
  (or (not required)
      omnipotent?
      (isa? level required)))

(defn- user-is?
  "Returns a cell whose value is true if the user is at the specified access
   level or higher, or if the user is omnipotent."
  [access-level-kw]
  (cell= (user-has-access? access-level-kw user-access-level omnipotent?)))

(def user-is-editor?
  (user-is? :access-level/editor))

(def user-is-ad-manager?
  (user-is? :access-level/ad-manager))

(def user-is-admin?
  (user-is? :access-level/admin))

(defn user-has-org-access?
  [required level]
  (or (not required)
      (isa? level required)))

(defn- user-org-access-is?
  "Returns a cell whose value is true if the user is at the specified access
   level or higher for the organization."
  [org-access-level-kw]
  (cell= (user-has-org-access? org-access-level-kw org-access-level)))

(def user-is-org-admin-read?
  (user-org-access-is? :org-access-level/admin-read))

(def user-is-org-not-set?
  (or
   (not org-access-level)
   (user-org-access-is? :org-access-level/not-set)))

;; special loaders ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defc flights-loading              nil)
(defc campaigns-loading            nil)
(defc advertiser-campaigns-loading nil)
(defc advertisers-loading          nil)
(defc advertiser-segments-loading  nil)
(defc segments-loading             nil)
(defc ads-loading                  nil)
(defc all-flights-loading          nil)
(defc all-ads-loading              nil)
(defc choose-network-loading       nil)

(defc= loading-cells
  [flights-loading
   advertiser-campaigns-loading
   advertiser-segments-loading
   segments-loading
   campaigns-loading
   advertisers-loading
   ads-loading
   all-flights-loading
   all-ads-loading
   choose-network-loading])

(defn tmp-flash-message!
  [msg]
  (reset! tmp-flash-message msg)
  (with-timeout 5000 (reset! tmp-flash-message nil)))

(defc= flash-message
  (cond (or (seq rpc-loading) (seq (filter seq loading-cells)))
        ~(from-lang-pack ::flash-message-loading)

        tmp-flash-message
        tmp-flash-message

        (contains? app-errors :rpc-timeout)
        ~(from-lang-pack ::flash-message-rpc-timeout)

        (contains? app-errors :external-server-error)
        ~(from-lang-pack ::flash-message-external-server-error)))

;; ClojureScript ex-info objects do not implement this protocol, which bizarrely
;; results in missing keys when you call `js->clj` on them.
;;
;; This fixes that, at least for the purposes of printing the exception object
;; like it's a ClojureScript map.
(extend-protocol IEncodeClojure
  ExceptionInfo
  (-js->clj [ex _]
    (reduce (fn [m k]
              (let [v (aget ex k)]
                (if (and (not (str/includes? k "$"))
                         (not (instance? js/Function v)))
                  (assoc m (keyword k) v)
                  m)))
            {}
            (js-keys ex))))

(defn pr-ex [ex endpoint]
  (when-let [{:keys [message serverStack cause data stack]} (js->clj ex)]
    (log/group- "RPC error: ~{message} ~{endpoint}"
                (when (or @omniscient? @omnipotent?)
                  (when cause (prn :cause cause))
                  (when data  (prn :data  data))))))

(defmulti global-rpc-error
  (fn [ex]
    (let [{:keys [data]} (js->clj ex)]
      (or (:pre data) (:error data))))
  :default ::default)

(defc networkid-from-hash nil)
(defc networkid-from-cookie nil)
(defc= networkid (or network
                     networkid-from-hash
                     networkid-from-cookie))

(do-watch networkid
          #(when %2
             (let [parts    (rest (str/split js/window.location.hash #"/"))]
               (set!  js/window.location.hash
                      (str/join "/" (concat ["#!" %2]
                                            (if (-> parts first util/->number)
                                              (rest parts)
                                              parts)))))))

(defn get-networkid-from-cookie
  []
  (util/->number (.get *cookies* "last-network")))

(defn initialize-networkid-cells!
  []
  (let [parts   (rest (str/split js/window.location.hash #"/"))]
    (when-let [network-id (-> parts
                              first
                              util/->number)]
      (reset! networkid-from-hash network-id))
    (reset! networkid-from-cookie (get-networkid-from-cookie))))

;; Bringing get-env up here feels weird because it's with the rest of the
;; remotes below.
(declare get-env)

(defn fetch-google-maps-ui-api-key!
  "Fetches the Google Maps JS API key from the backend and stashes the result in
  `hoplon.google.jsapi.loader/api-key`."
  []
  (-> (get-env "ADZERK_GOOGLE_MAPS_UI_API_KEY")
      (util/on-change #(when % (reset! google-jsapi-loader/api-key %)))))

(defn fetch-static-asset-domain!
  "Fetches the creative static-asset domain (required backend env var
  KEVEL_GEIR_STATIC_ASSET_DOMAIN) and stashes it in
  `ui.global-state/static-asset-domain`."
  []
  (-> (get-env "KEVEL_GEIR_STATIC_ASSET_DOMAIN")
      (util/on-change #(when (seq %) (reset! global/static-asset-domain %)))))

(defc= rpc-additional-headers
  (merge
   {}
   (when networkid
     {"X-Kevel-NetworkId" networkid})))

(defn rpc-opts
  [endpoint]
  {:credentials         true
   :timeout             30000
   :additional-headers  rpc-additional-headers
   :on-error            #(do (pr-ex % endpoint) (global-rpc-error %))
   :url                 ADZERK_GEIR_BACKEND_URL})

(def multipart-opts {:clj->json clj->form-data
                     :ajax-fn   multipart-ajax-fn
                     ;; Creative uploads can be up to 5MB (see
                     ;; geir-backend.validation/MAX_FILE_SIZE_OVERRIDE), which can
                     ;; take longer than the default 30s RPC timeout on a slow
                     ;; connection.
                     :timeout   120000})

(defn remote [endpoint & {:keys [state loading error opts]}]
  (with-meta
    (mkremote (if (namespace endpoint)
                endpoint
                (symbol "geir-backend.rpc" (name endpoint)))
              (or state   (cell nil))
              (or error   (cell nil))
              (or loading rpc-loading)
              (merge (update (rpc-opts endpoint)
                             :url #(str % "/" (name endpoint)))
                     opts))
    {:endpoint endpoint}))

(defn action [endpoint page & [remote-opts]]
  (let [loading (or (:loading remote-opts) (cell nil))]
    (remote endpoint :state (:data page) :loading loading :opts (dissoc remote-opts :loading))))

(def ^:const DUPLICATE-CAMPAIGN-MAX-FLIGHTS 100)

(def ^:const MAX-ZONES
  "A practical limit on the number of zones to fetch if we want to fetch \"all\"
   of a network's zones, including all site-zones and all network-zones.

   One use case is the zone typeahead on the \"new report\" screen. One way to
   use the typeahead is to start typing in the name of a zone you have in mind.
   Another way, assuming you don't have a lot of zones, is to scroll down until
   you see the zone you want. When you first click on the typeahead, it fetches
   results for the empty string, i.e. \"all\" of your zones, so it's helpful if
   we can actually attempt to do something like that.

   As of 2019-10-11, 90% of active networks have < 100 zones, 6% have 100-500,
   and 4% have > 500."
  500)

;; paginated remotes ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defp salespeople             (remote 'salespeople))
(defp sites                   (remote 'sites))
(defp flight-priorities       (remote 'priorities))
(defp sbc-site-select         (remote 'sites-not-in-channel)
  :sorting {:by :title :order :asc} :page-size 10)
(defp flights-csv             (remote 'flights-csv :opts {:timeout 120000}))
(defp all-flights-csv         (remote 'flights-csv :opts {:timeout 300000}))
(defp all-ads-csv             (remote 'all-ads-csv :opts {:timeout 300000}))
(defp campaigns               (remote 'campaigns :loading campaigns-loading)
  :sorting {:by :id :order :desc})
(defp advertiser-campaigns    (remote 'campaigns :loading advertiser-campaigns-loading)
  :sorting {:by :id :order :desc})
(defp flights                 (remote 'flights :loading flights-loading))
(defp duplicate-flights       (remote 'flights)
  ;; We get one more than our max duplication limit, so when the user attempts to
  ;; duplicate a campaign, we'll know if they are above that limit if we were
  ;; successfully able to fetch more than the max during this load.
  :page-size (inc DUPLICATE-CAMPAIGN-MAX-FLIGHTS))
(defp all-flights             (remote 'flights :loading all-flights-loading :opts {:timeout 120000}))
(defp all-ads                 (remote 'all-ads :loading all-ads-loading :opts {:timeout 120000}))
(defp current-flight          (remote 'flight))
(defp advertisers             (remote 'advertisers :loading advertisers-loading))
(defp all-assets              (remote 'all-assets))
(defp assets-by-advertiser    (remote 'assets-by-advertiser))
(defp ad-chains               (remote 'ad-chains)
  :sorting {:by :name :order :asc})
(defp creatives               (remote 'creatives)
  :sorting {:by :title :order :asc})
(defp creatives-by-flight     (remote 'creatives-by-flight :loading ads-loading)
  :sorting {:by :title :order :asc})

(defp geometries              (remote 'list-geometries)
  :sorting {:by :id :order :asc}
  :page-size 25)

(defp segments                (remote 'segments :loading segments-loading))
(defp countries               (remote 'countries))
(defp scheduled-reports       (remote 'scheduled-reports))
(defp parent-counts-by-flight (remote 'parent-counts-by-flight))
(defp rtb-partners            (remote 'rtb-partners)
  :page-size 250
  :sorting {:by :name :order :asc})

(defp flights-by-ad-chain-ordered   (remote 'flights-by-ad-chain)
  :sorting {:by :passbackpriority :order :asc}
  :page-size 250)
(defp flights-by-ad-chain-optimized (remote 'flights-by-ad-chain)
  :sorting {:by :effectivecpm :order :desc})

(defc current-channel        nil)
(defc current-site           nil)
(defc current-campaign       nil)
(defc current-flight-channel nil)
(defc current-advertiser     nil)
(defc current-ad-chain       nil)
;; the flight for the selected ad in app.ui.campaigns.all-ads
(defc ad-flight              nil)
;; the flight for which ad code is being generated in app.ui.ad-code
(defc ad-code-flight         nil)
;; the ad for which ad code is being generated in app.ui.ad-code
(defc ad-code-ad             nil)

;; NOTE: changed page size to 100 to accomodate networks that have more than
;;       default of 25 items for a paged view. Maximum as of this note (2/7/2020)
;;       seems to be in the low 30s. If this number grows we may want to consider
;;       a typeahead or other mechanism for choosing from among many choices.
(defp creative-templates (remote 'creative-templates)
  :page-size 100)

;; FIXME: MAX-AD-TYPES is a temporary workaround; as of 2015-07-29 no network is
;;        associated with more than 300 ad types. We do this to get all ad types
;;        without pagination so we can populate a form with ad type options.
(def ^:const MAX-AD-TYPES 500)

(defp adtypes
  (remote 'adtypes)
  :page-size MAX-AD-TYPES
  :sorting   {:by :id :order :asc})

(defp priority-adtypes
  (remote 'priority-adtypes)
  :page-size MAX-AD-TYPES
  :sorting   {:by :id :order :asc})

(defp site-adtypes
  (remote 'site-adtypes)
  :page-size MAX-AD-TYPES
  :sorting   {:by :id :order :asc})

;; remotes ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn get-env
  "Call rpc/get-env on the backend to retrieve the value of the env
  variable corresponding with env-key from the backend. Return a cell
  that will contain the value when returned. This value might be a map
  of values keyed by their name."
  [& names]
  (with-let [s (cell nil)]
    ((remote 'get-env :state s) names)))

(def fetch-language-pack!
  (remote 'language-pack
          :state   language-pack
          :loading (cell nil)
          :opts    (update (rpc-opts 'language-pack) :url #(str % "/language-pack"))))

(def session-ping
  (remote 'session-info
          :state   session-info
          :loading (cell nil)
          :opts    (update (rpc-opts 'session-info) :url #(str % "/session-ping"))))

;; Define cells to store the relevant versions of the Studio built assets
(defc studio-js nil)
(defc studio-css nil)

(defn- fetch-studio-config
  "Fetch the studio asset config info from the config file on S3.
   
   There are currently 2 hosted versions of Studio:
   - https://studio-frontend.kevel.co in prod
   - https://studio-frontend.mdev.opzerk.com in mdev"
  []
  (let [studio-env (condp = global/GEIR_ENVIRONMENT
                     :gcp-prod :prod
                     :aws-prod :prod
                     ;; The Studio build is normally served from
                     ;; https://studio-frontend.kevel.co in prod, and
                     ;; https://studio-frontend.mdev.opzerk.com in mdev and when
                     ;; developing locally.
                     ;;
                     ;; When developing changes for Studio, you may find it helpful to
                     ;; try them out in your local Geir setup. To do this, you can run
                     ;; `yarn release` in the studio-frontend repo and copy the built
                     ;; js into the Geir repo (where it is gitignored):
                     ;;
                     ;;   cd ../studio-frontend
                     ;;   yarn release
                     ;;   cd ../geir
                     ;;   cp ../studio-frontend/target/js/studio.js ui/js/studio.js
                     ;;
                     ;; Then, un-comment this line to make the application use your
                     ;; local version of Studio instead of the one that's currently
                     ;; deployed:
                     ;;
                     ;; :local :dev
                     :mdev)
        response (js/fetch
                  (if (= studio-env :prod)
                    "https://studio-frontend.kevel.co/studio.json"
                    "https://studio-frontend.mdev.opzerk.com/studio.json")
                  (clj->js
                   {:headers {"Cache-Control" "no-store"}}))]
    (-> response
        (.then #(.json %))
        (.then #(js->clj % :keywordize-keys true))
        (.then #(get-in % [:assets studio-env]))
        (.then (fn [config]
                 (reset! studio-js (:studiojs config))
                 (reset! studio-css (:studiocss config)))))))

(fetch-studio-config)

(defn- safely-update-user-and-network-info
  "Handle the response from a 'user and network info' ping request.
   If the network ID received doesn't match what's already there,
   this indicates that the data was updated while the request was
   in flight (e.g. by the user choosing a network), and the ping
   request should be discarded.
   
   However, if the new network ID is nil, this means the user lost
   access to their network, and we should accept the update."
  [response]
  (swap!
   user-and-network-info
   (fn [old-un-info]
     (let [new-network-id (get-in response [:user :networkid])
           old-network-id (get-in old-un-info [:user :networkid])]
       (cond
         ;; Network IDs match - accept update
         (= new-network-id old-network-id)
         response

         ;; User lost access to network (new network is nil) - accept update
         (nil? new-network-id)
         response

         ;; Network IDs don't match - discard update (changed in another tab)
         :else
         (do
           (.warn js/console
                  "User/network ping data discarded: network IDs do not match."
                  (clj->js
                   {:old-network (select-keys
                                  (:user old-un-info)
                                  [:networkid :networkname])
                    :new-network (select-keys
                                  (:user response)
                                  [:networkid :networkname])}))
           old-un-info))))))

(declare logout)

(def user-network-ping
  (remote 'user-and-network-info
          :state   (util/trigger safely-update-user-and-network-info)
          :loading (cell nil)
          :opts    (-> (rpc-opts 'user-and-network-info)
                       (merge {:on-error #(do
                                            (pr-ex % 'user-and-network-info)
                                            (global-rpc-error %)
                                            (when-let [error-code (get-in (js->clj %) [:data :error-code])]
                                              (when (= error-code :invalid-login)
                                                (logout))))})
                       (update :url #(str % "/user-network-ping")))))

(def keepalive
  "Variant of `ping` that will update the 'last RPC time' value on
   the session (unlike ping), preventing the session from timing
   out due to inactivity."
  (remote 'session-info
          :state session-info
          :opts  (update (rpc-opts 'session-info)
                         :url
                         #(str % "/keepalive"))))

(defn authenticate-with-credentials
  [& args]
  (apply (remote 'authenticate-with-credentials) args))

;; TODO: Delete once we've migrated to the new auth flow
(defn authenticate-with-credentials-old
  [& args]
  (apply (remote 'authenticate-with-credentials-old) args))

(defn- auth-logout-url
  "Constructs the complete auth provider logout URL, given a base URL already
   containing the id token hint param.

   This comprises the base URL and the value of the `post_logout_redirect_uri`
   param, which is an encoded URL that the auth provider redirects back to after
   successful logout."
  [{:keys [base-logout-url]}]
  (let [location (-> (url (.. js/window -location -href))
                     (assoc :path "")
                     (assoc :query nil)
                     (assoc :anchor nil))]
    (if base-logout-url
      (str base-logout-url (-> location str js/encodeURIComponent))
      (str location))))

(defn logout []
  (-> ((remote 'logout))
      (.then
       (fn [logout-url-config]
         (set! (.. js/window -location -href)
               (auth-logout-url logout-url-config))))))

;; SSO ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn organization-for-code
  [form]
  ((remote 'organization-for-code) form))

(defc choose-network-error nil)

(defn- safely-swap-in-chosen-network-data!
  [{:keys [network-and-user feature-flags custom-fields]}]
  ;; FIXME: It's a little weird that the entry we're calling `:user` is actually
  ;; information about both the network and the user.
  (let [new-data {:user          network-and-user
                  :feature-flags feature-flags
                  :custom-fields custom-fields}]
    (util/swap-if! user-and-network-info
                   #(not= % :ui.global-state/unknown)
                   #(global/update-user-and-network-info % new-data)
                   (constantly new-data))))

(defn choose-network [{:keys [network-id] :as network}]
  ((remote 'choose-network
           :state  (util/trigger safely-swap-in-chosen-network-data!)
           :opts   {:additional-headers
                    (cell=
                     (merge
                      rpc-additional-headers
                      {"X-Kevel-NetworkId" network-id}))}
           :loading choose-network-loading
           :error   choose-network-error)
   network))

(defn get-session []
  ((remote 'get-auth-session)))

(defn get-networks-for-user []
  ((remote 'networks)))

;; TODO: Delete once we've migrated to the new auth flow
(defn log-cognito-negative-response
  [params]
  ((remote 'log-cognito-negative-response) params))

(defn log-auth-negative-response
  [params]
  ((remote 'log-auth-negative-response) params))

(defn record-metric!
  [& params]
  (apply (remote 'record-metric!) params))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(def set-preferences!             (remote 'set-preferences!))
(def upsert-flight!               (action 'upsert-flight! current-flight {:timeout 120000 :loading flights-loading}))
(def delete-flight!               (action 'delete-flight! all-flights {:timeout 120000 :loading all-flights-loading}))
(def duplicate-flight!            (partial action 'duplicate-flight!))
(def delete-campaign!             (action 'delete-campaign! campaigns {:loading campaigns-loading}))
(def upsert-creative!             (action 'upsert-creative! creatives multipart-opts))
(def upsert-ad!                   (action 'upsert-creative! all-ads multipart-opts))
(def remove-creative-from-flight! (action 'remove-creative-from-flight! creatives-by-flight))
(def remove-creative!             (action 'delete-ad! all-ads {:timeout 120000 :loading all-ads-loading}))
(def delete-creative!             (action 'delete-creative! creatives))
(def set-campaign-activeness!     (remote 'set-campaign-activeness! :state (:data campaigns)))
(def set-flight-activeness!       (remote 'set-flight-activeness!   :state (:data flights)))
(def create-asset-by-advertiser!  (action 'create-asset-by-advertiser! (cell nil)))

;; init ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; reset global version and error states after every RPC call
(let [xhr-version #(.getResponseHeader % "X-Geir-Version")]
  (.ajaxSetup jquerying (js-obj "complete" #(reset! backend-version (xhr-version %1))
                                "success"  #(swap! app-errors empty)
                                "error"    #(when (= "timeout" (.-statusText %))
                                              (swap! app-errors conj :rpc-timeout)))))

(def ^:const SESSION-PING-INTERVAL-MS 10000)
(def ^:const USER-NETWORK-PING-INTERVAL-MS 30000)

(defn enable-ping!-factory
  "Creates a function that will send a ping to the backend periodically when the
   user is logged in, refreshing data relevant to the session.

   The login flow uses multiple pings and then merges their data into a single
   map."
  [ping-fn interval-ms]
  (fn []
    (let [interval-id-c (cell nil)]
      (do-watch logged-in?
                (fn [_ logged-in]
                  (if logged-in
                    (reset! interval-id-c
                            (.setInterval js/window #(ping-fn) interval-ms))
                    (when-let [interval-id  @interval-id-c]
                      (.clearInterval js/window interval-id))))))))

(def enable-session-age-ping!
  (enable-ping!-factory session-ping SESSION-PING-INTERVAL-MS))

(def enable-user-network-ping!
  (enable-ping!-factory user-network-ping USER-NETWORK-PING-INTERVAL-MS))

(defc loaded-etag nil)
(defc current-etag nil)

(defc= etag-mismatch?
  (and loaded-etag current-etag (not= loaded-etag current-etag)))

;; Poll to see if the frontend version has been updated since page load. We use
;; this information to tell the user when the application has been updated so
;; that they can save their work and refresh to get the new version.
(with-interval 30000
  (let [opts {"async"       true
              "contentType" false
              "processData" false
              "type"        "GET"
              "url"         (-> js/window .-location .-origin)}]
    (-> (.ajax jquerying (clj->js opts))
        (.fail (fn [_ _ _]
                 (js/console.error "Failed to check for a new ETag.")))
        (.done (fn [_ _ x]
                 (let [{:strs [ETag]} (xhr-resp-headers x ["ETag"])]
                   (dosync
                    (reset! current-etag ETag)
                    (swap! loaded-etag #(or % ETag)))))))))

;; global rpc error handlers ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defmethod global-rpc-error ::default     [ex] nil)
;;; TODO: Determine if we should do anything more than just logging with the next 2 errors...
(defmethod global-rpc-error :have-network [ex] nil)
(defmethod global-rpc-error :have-api-key [ex] nil)
(defmethod global-rpc-error :logged-in    [ex] (reset! global/user-and-network-info ::global/unknown))

;; This handler is called when an external service (Management API, Instant
;; Counts API, Reporting API, etc.) unexpectedly fails to return a response that
;; Geir can use.
(defmethod global-rpc-error :external-server-error [_]
  (swap! app-errors conj :external-server-error))

;; custom elements ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn if-feature
  [feature elem-if elem-else]
  (let [feature-enabled? (cell= (get feature-flags feature))]
    (vector
     (elem-if :toggle feature-enabled?)
     (elem-else :toggle (cell= (not feature-enabled?))))))

(defn when-feature
  {:style/indent :defn}
  [feature elem]
  (elem :toggle (cell= (get feature-flags feature))))

(defn when-not-feature
  [feature elem]
  (elem :toggle (cell= (not (get feature-flags feature)))))

;;

(defn toggle-handler
  [endpoint entity {:keys [data] :as page} & [{:keys [refresh-pages active-key]
                                               :or   {active-key :isactive}}]]
  #(let [active? (cell= (get entity active-key))]
     (-> ((remote endpoint :state data)
          @entity (not @active?) (p/page-opts page))
         (.fail (fn [ex] (tmp-flash-message! (ex-message ex))))
         (.done (fn [_] (doseq [page refresh-pages]
                          (do
                            (p/activate! page)
                            (p/refresh! page))))))))

;;;;; Batch insertion helpers

(defn process-work-item!
  "Pull pieces of work from a work queue and apply an RPC
   function to them. The pieces of work should include
   :data (the data to pass to the RPC) and :resolve (the
   function to call to indicate to the caller that this
   piece of work is done and pass back the result)."
  [rpc-fn work-queue]
  (go
    (when-let [{:keys [data _retries resolve] :as _work}
               (<! work-queue)]
      (try
        ;; TODO: implement retrying, as appropriate
        (-> (rpc-fn data)
            (.done #(resolve {:status :success
                              :data   %}))
            (.catch #(resolve {:status :error
                               :error  %
                               :data   data}))
            (.always #(process-work-item! rpc-fn work-queue)))
        (catch js/Error e
          (resolve {:status :error
                    :error  e
                    :data   data})
          (process-work-item! rpc-fn work-queue))))))

(defn rpc-all!
  "Apply `rpc-fn` to all items in `data` in batches of
   size `max-concurrency`."
  [rpc-fn data & {:keys [max-concurrency]
                  :or   {max-concurrency 5}}]
  (let [work-queue  (chan)
        all-results (doall
                     (for [item data]
                       (js/Promise.
                        (fn [resolve _reject]
                          (go (>! work-queue
                                  {:data    item
                                   :retries 0
                                   :resolve resolve}))))))]
    (dotimes [_ max-concurrency]
      (process-work-item! rpc-fn work-queue))

    (-> all-results
        js/Promise.all
        (.then #(group-by :status %))
        (.catch #(js/console.error %))
        (.finally #(close! work-queue)))))
